import { BookOpen, GraduationCap, Puzzle, Palette, ArrowRight } from "lucide-react";
import { Link } from "react-router-dom";
import PageHero from "@/components/PageHero";
import Newsletter from "@/components/Newsletter";
import { FadeIn, ChapterLabel } from "@/components/Reveal";
import useSEO from "@/hooks/useSEO";
import { SKOOL_URL } from "@/data/content";

const PILLARS = [
  {
    icon: BookOpen,
    title: "The Reading & Watch Club",
    desc: "A gentle monthly gathering for ages 4-8, where we watch the new read-aloud together and talk about the places we carry.",
    tag: "Open to all",
  },
  {
    icon: GraduationCap,
    title: "Classroom Resources",
    desc: "Mentor texts for kindness and self-belief, classroom read-aloud discussion guides for kindergarten and early elementary, and creative prompts that bring the stories into your lessons.",
    tag: "Coming soon",
  },
  {
    icon: Puzzle,
    title: "Downloadable Activities",
    desc: "Printable activity sheets for ages 4-8, from colouring pages and map-making sheets to 'draw your own adventure' templates for rainy afternoons.",
    tag: "Coming soon",
  },
  {
    icon: Palette,
    title: "Fan Art Gallery",
    desc: "Young artists draw their favourite moments, and we frame the stories through their eyes.",
    tag: "Coming soon",
  },
];

export default function Community() {
  useSEO(
    "Classroom Read-Aloud Guides & Activities for Ages 4-8 | The Cap Chronicles",
    "Classroom resources for teachers: mentor texts for kindness, read-aloud discussion guides for kindergarten and early elementary, plus printable activities for ages 4-8."
  );
  return (
    <main>
      <PageHero
        kicker="Community"
        title="The journey is better"
        italic="together."
        description="The Cap Chronicles is growing into a place for families, classrooms, and young artists. Here's what's being built around the campfire."
      />

      <section className="mx-auto max-w-7xl px-6 pb-24 md:px-12 md:pb-32" data-testid="community-pillars">
        <ChapterLabel num="01" title="Around the Campfire" />
        <div className="grid gap-6 sm:grid-cols-2">
          {PILLARS.map((p, i) => (
            <FadeIn key={p.title} delay={i * 0.08}>
              <div className="group h-full rounded-3xl border border-charcoal/10 bg-white p-8 transition-shadow duration-500 hover:shadow-[0_30px_60px_-25px_rgba(47,107,60,0.25)]" data-testid={`community-card-${i}`}>
                <div className="flex items-start justify-between">
                  <span className="flex h-12 w-12 items-center justify-center rounded-2xl bg-forest/10 text-forest transition-colors duration-300 group-hover:bg-forest group-hover:text-cream">
                    <p.icon size={20} />
                  </span>
                  <span className={`rounded-full px-3 py-1 text-[11px] font-semibold uppercase tracking-widest ${p.tag === "Open to all" ? "bg-gold/20 text-charcoal" : "bg-charcoal/5 text-charcoal/50"}`}>{p.tag}</span>
                </div>
                <h2 className="mt-6 font-serif text-2xl tracking-tight">{p.title}</h2>
                <p className="mt-3 text-sm leading-relaxed text-charcoal/65 md:text-base">{p.desc}</p>
              </div>
            </FadeIn>
          ))}
        </div>

        <FadeIn delay={0.15} className="mt-14 flex flex-col items-start gap-5">
          <a href={SKOOL_URL} target="_blank" rel="noopener noreferrer" data-testid="community-skool-button" className="group inline-flex items-center gap-3 rounded-full bg-forest px-8 py-4 text-sm font-semibold uppercase tracking-widest text-cream transition-transform duration-300 hover:scale-[1.03]">
            Join the story club on Skool <ArrowRight size={15} className="transition-transform duration-300 group-hover:translate-x-1" />
          </a>
          <Link to="/contact" data-testid="community-get-involved-link" className="group inline-flex items-center gap-2 text-sm font-semibold uppercase tracking-widest text-forest">
            Want to bring the Chronicles to your school or library? Get in touch
            <ArrowRight size={15} className="transition-transform duration-300 group-hover:translate-x-1" />
          </Link>
        </FadeIn>
      </section>

      <Newsletter />
    </main>
  );
}
